import { useCallback, useEffect, useState } from "react";
import { apiGet, apiPut } from "@/lib/api";
import { ADMIN_NAV_DIV, ADMIN_NAV_SUPER, buildAdminNav, type AdminNavEntry, type AdminNavOrder } from "@/lib/admin-nav";

const EVENT = "admin-nav-order-changed";
const storageKey = (userId: string) => `pph-admin-nav-order:${userId}`;

/** What this browser last knew (a cache so the menu draws instantly); the account on the server is the truth. */
export function readAdminNavOrder(userId: string | undefined): AdminNavOrder | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AdminNavOrder>;
    return { top: Array.isArray(parsed.top) ? parsed.top : [], setup: Array.isArray(parsed.setup) ? parsed.setup : [] };
  } catch {
    return null;
  }
}

function writeCache(userId: string, order: AdminNavOrder | null) {
  try {
    if (order) localStorage.setItem(storageKey(userId), JSON.stringify(order));
    else localStorage.removeItem(storageKey(userId));
  } catch {
    // storage blocked: the cache just isn't kept
  }
  window.dispatchEvent(new Event(EVENT));
}

// One fetch per user per page load, shared by the sidebar, the phone tabs and the landing redirect.
const loads = new Map<string, Promise<AdminNavOrder | null>>();

/**
 * The admin's saved order from their account. An order saved on this device before it moved to the
 * account is uploaded once, so nobody loses what they set up.
 */
export function loadAdminNavOrder(userId: string | undefined): Promise<AdminNavOrder | null> {
  if (!userId) return Promise.resolve(null);
  let p = loads.get(userId);
  if (!p) {
    p = (async () => {
      try {
        const res = await apiGet<{ data: AdminNavOrder | null }>("/api/me/admin-nav-order");
        if (res.data) {
          writeCache(userId, res.data);
          return res.data;
        }
        const local = readAdminNavOrder(userId);
        if (local) {
          await apiPut("/api/me/admin-nav-order", { order: local });
          return local;
        }
        writeCache(userId, null);
        return null;
      } catch {
        loads.delete(userId); // try again next time (offline, signed out, ...)
        return readAdminNavOrder(userId);
      }
    })();
    loads.set(userId, p);
  }
  return p;
}

/**
 * The signed-in admin's own order of the Admin pages. Saved on their account, so it is the same on
 * every device and every way of signing in; the sidebar, the phone tab strip and the customize dialog
 * all read it, so a change shows everywhere at once.
 */
export function useAdminNavOrder(userId: string | undefined) {
  const [order, setOrderState] = useState<AdminNavOrder | null>(() => readAdminNavOrder(userId));

  useEffect(() => {
    setOrderState(readAdminNavOrder(userId));
    const onChange = () => setOrderState(readAdminNavOrder(userId));
    window.addEventListener(EVENT, onChange);
    void loadAdminNavOrder(userId);
    return () => window.removeEventListener(EVENT, onChange);
  }, [userId]);

  const setOrder = useCallback(
    (next: AdminNavOrder | null) => {
      if (!userId) return;
      writeCache(userId, next); // show it now
      loads.set(userId, Promise.resolve(next));
      void apiPut("/api/me/admin-nav-order", { order: next }).catch(() => {
        // Not saved on the account (offline?). It stays on this device and is uploaded next time.
        loads.delete(userId);
      });
    },
    [userId]
  );

  return { order, setOrder };
}

/** The first page of the Admin list, in this admin's own order (where the Admin tab lands). */
export function firstAdminPath(role: string | null | undefined, order: AdminNavOrder | null, entries?: AdminNavEntry[]): string {
  const list = entries ?? buildAdminNav(role === "admin" ? ADMIN_NAV_SUPER : ADMIN_NAV_DIV, order);
  const first = list[0];
  if (!first) return "/admin/users";
  return first.kind === "item" ? first.item.to : first.items[0]?.to ?? "/admin/users";
}
